import {
  formatDateKey,
  formatOrderDate,
  normalizeOrderStatus,
  statusLabel,
} from "../admin/ordersPage";

export const MANILA_TIME_ZONE = "Asia/Manila";
export const PDF_DETAIL_ROW_LIMIT = 40;

const pad2 = (value) => String(value).padStart(2, "0");

export const compactDateKey = (key) => String(key || "").replace(/-/g, "");

export const manilaDateKeyNow = (now = new Date()) =>
  now.toLocaleDateString("en-CA", { timeZone: MANILA_TIME_ZONE });

export const shiftDateKey = (key, days) => {
  const [year, month, day] = String(key || "").split("-").map(Number);
  if (!year || !month || !day) return "";
  const shifted = new Date(Date.UTC(year, month - 1, day + Number(days || 0)));
  return `${shifted.getUTCFullYear()}-${pad2(shifted.getUTCMonth() + 1)}-${pad2(shifted.getUTCDate())}`;
};

export const defaultLast30ManilaDays = (now = new Date()) => {
  const to = manilaDateKeyNow(now);
  return { from: shiftDateKey(to, -29), to };
};

export const inManilaDateRange = (dateInput, fromKey, toKey) => {
  if (!fromKey && !toKey) return true;
  const key = formatDateKey(dateInput);
  if (!key) return false;
  if (fromKey && key < fromKey) return false;
  if (toKey && key > toKey) return false;
  return true;
};

export const filterOrdersByManilaRange = (orders, fromKey, toKey) =>
  (orders || []).filter((order) => inManilaDateRange(order?.purchaseDate, fromKey, toKey));

export const summarizeOrders = (orders = []) => {
  const statusCounts = {
    paid: 0,
    pending: 0,
    failed: 0,
    cancelled: 0,
    expired: 0,
  };
  const eventTotals = new Map();

  let paidOrderCount = 0;
  let paidTicketCount = 0;
  let paidRevenue = 0;

  (orders || []).forEach((order) => {
    const status = normalizeOrderStatus(order?.status);
    statusCounts[status] = (statusCounts[status] || 0) + 1;

    if (status !== "paid") return;

    const quantity = Number(order?.quantity || 0);
    const amount = Number(order?.total || 0);
    paidOrderCount += 1;
    paidTicketCount += Number.isFinite(quantity) ? quantity : 0;
    paidRevenue += Number.isFinite(amount) ? amount : 0;

    const eventName = String(order?.event || "Unknown Event");
    const current = eventTotals.get(eventName) || { event: eventName, orderCount: 0, ticketCount: 0, revenue: 0 };
    current.orderCount += 1;
    current.ticketCount += Number.isFinite(quantity) ? quantity : 0;
    current.revenue += Number.isFinite(amount) ? amount : 0;
    eventTotals.set(eventName, current);
  });

  return {
    rowCount: orders.length,
    paidOrderCount,
    paidTicketCount,
    paidRevenue,
    statusCounts,
    byEvent: [...eventTotals.values()].sort((a, b) => b.revenue - a.revenue || a.event.localeCompare(b.event)),
  };
};

export const formatRangeLabel = (fromKey, toKey) => {
  if (!fromKey && !toKey) return "All dates";
  if (fromKey && toKey) return `${fromKey} to ${toKey}`;
  if (fromKey) return `From ${fromKey}`;
  return `Through ${toKey}`;
};

export const ordersReportFilename = (fromKey, toKey, ext = "csv") => {
  const suffix = String(ext || "csv").replace(/^\./, "");
  if (!fromKey && !toKey) return `orders-all-dates.${suffix}`;
  const start = compactDateKey(fromKey || toKey);
  const end = compactDateKey(toKey || fromKey);
  return `orders-${start}-${end}.${suffix}`;
};

const csvEscape = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;

const formatPurchaseTimestamp = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleString("en-PH", { timeZone: MANILA_TIME_ZONE });
};

const formatStatusCounts = (statusCounts = {}) =>
  Object.entries(statusCounts)
    .filter(([, count]) => Number(count) > 0)
    .map(([status, count]) => `${statusLabel(status)}=${count}`)
    .join("; ");

export const buildOrdersCsv = ({
  orders = [],
  summary,
  fromKey = "",
  toKey = "",
  generatedAt = new Date(),
} = {}) => {
  const totals = summary || summarizeOrders(orders);
  const generated = generatedAt instanceof Date ? generatedAt.toISOString() : String(generatedAt || "");
  const meta = [
    ["Report", "Echo Jham Orders"],
    ["Generated at", generated],
    ["Date range", formatRangeLabel(fromKey, toKey)],
    ["Paid orders", totals.paidOrderCount],
    ["Paid tickets", totals.paidTicketCount],
    ["Paid revenue (PHP)", totals.paidRevenue],
    ["Status counts", formatStatusCounts(totals.statusCounts)],
    ["Rows exported", totals.rowCount],
  ];

  const headers = [
    "order_code",
    "customer",
    "email",
    "event",
    "purchase_date",
    "quantity",
    "amount",
    "status",
  ];

  const rows = (orders || []).map((order) => [
    order.orderCode || "",
    order.customer || "",
    order.customerEmail || "",
    order.event || "",
    formatPurchaseTimestamp(order.purchaseDate),
    Number(order.quantity || 0),
    Number(order.total || 0),
    statusLabel(order.status),
  ]);

  return [
    ...meta.map((pair) => pair.map(csvEscape).join(",")),
    "",
    headers.join(","),
    ...rows.map((row) => row.map(csvEscape).join(",")),
  ].join("\n");
};

const formatPhp = (value) => `PHP ${Number(value || 0).toLocaleString("en-PH")}`;

export const buildOrdersReportPdf = ({
  orders = [],
  summary,
  fromKey = "",
  toKey = "",
  generatedAt = new Date(),
} = {}) => {
  const totals = summary || summarizeOrders(orders);
  const includeDetailTable = orders.length <= PDF_DETAIL_ROW_LIMIT;
  const { jsPDF } = require("jspdf");
  const doc = new jsPDF({
    unit: "pt",
    format: "a4",
    orientation: includeDetailTable && orders.length > 0 ? "landscape" : "portrait",
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 40;
  let y = 42;

  doc.setFillColor(139, 0, 0);
  doc.rect(0, 0, pageWidth, 56, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("Echo Jham Orders Report", margin, 34);

  y = 80;
  doc.setTextColor(40, 40, 40);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  const generatedLabel = generatedAt instanceof Date
    ? generatedAt.toLocaleString("en-PH", { timeZone: MANILA_TIME_ZONE })
    : String(generatedAt);
  doc.text(`Date range: ${formatRangeLabel(fromKey, toKey)}`, margin, y);
  y += 16;
  doc.text(`Generated: ${generatedLabel}`, margin, y);
  y += 16;
  doc.text("Paid revenue uses completed checkouts in this range.", margin, y);
  y += 28;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text(`Paid orders: ${totals.paidOrderCount}`, margin, y);
  doc.text(`Paid tickets: ${totals.paidTicketCount}`, margin + 180, y);
  doc.text(`Paid revenue: ${formatPhp(totals.paidRevenue)}`, margin + 360, y);
  y += 18;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(`Status counts: ${formatStatusCounts(totals.statusCounts) || "none"}`, margin, y);
  y += 28;

  const ensureSpace = (needed) => {
    if (y + needed < pageHeight - margin) return;
    doc.addPage();
    y = margin;
  };

  if (includeDetailTable && orders.length > 0) {
    doc.setFont("helvetica", "bold");
    doc.text("Orders", margin, y);
    y += 14;
    const columns = [
      { key: "order", x: margin, width: 80 },
      { key: "customer", x: margin + 80, width: 110 },
      { key: "event", x: margin + 190, width: 150 },
      { key: "date", x: margin + 340, width: 90 },
      { key: "qty", x: margin + 430, width: 40 },
      { key: "amount", x: margin + 470, width: 90 },
      { key: "status", x: margin + 560, width: 80 },
    ];
    doc.setFontSize(8);
    doc.text("Order", columns[0].x, y);
    doc.text("Customer", columns[1].x, y);
    doc.text("Event", columns[2].x, y);
    doc.text("Date", columns[3].x, y);
    doc.text("Qty", columns[4].x, y);
    doc.text("Amount", columns[5].x, y);
    doc.text("Status", columns[6].x, y);
    y += 10;
    doc.setDrawColor(200, 200, 200);
    doc.line(margin, y, pageWidth - margin, y);
    y += 12;
    doc.setFont("helvetica", "normal");

    orders.forEach((order) => {
      ensureSpace(16);
      const cells = [
        String(order.orderCode || ""),
        String(order.customer || ""),
        String(order.event || ""),
        formatOrderDate(order.purchaseDate),
        String(order.quantity || 0),
        formatPhp(order.total),
        statusLabel(order.status),
      ];
      cells.forEach((text, index) => {
        doc.text(doc.splitTextToSize(text, columns[index].width - 4)[0] || "", columns[index].x, y);
      });
      y += 14;
    });
  } else {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("Paid totals by event", margin, y);
    y += 16;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    if (orders.length > PDF_DETAIL_ROW_LIMIT) {
      doc.text("This PDF is a summary. Download CSV for the full order list.", margin, y);
      y += 18;
    }
    if (totals.byEvent.length === 0) {
      doc.text("No paid orders in this range.", margin, y);
    } else {
      totals.byEvent.forEach((row) => {
        ensureSpace(16);
        doc.text(
          `${row.event}  ${row.orderCount} orders  ${row.ticketCount} tickets  ${formatPhp(row.revenue)}`,
          margin,
          y
        );
        y += 14;
      });
    }
  }

  return doc;
};

export const downloadTextFile = (contents, filename, mimeType = "text/csv;charset=utf-8;") => {
  const blob = new Blob([contents], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
